import { useCallback, useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import { InfiniteScrollConceptAutocomplete } from "../../../components/RegulationsTable/FilterBar/InfiniteScrollConceptAutocomplete";
import { aiPlanFilterFieldSx } from "../../../constants/regulationDetailsNavTabStyles";
import { usePaginatedConceptValues } from "../../../hooks/usePaginatedConceptValues";
import {
  fetchConditionalPlanOutputConcepts,
  type ConfigurableConcept,
} from "../../../services/autoTaggingService";
import type { ConceptValue } from "../../../services/regulationsService";

const CONCEPT_SEARCH_DEBOUNCE_MS = 300;
const CONCEPT_PAGE_SIZE = 20;

const formFieldLabelSx = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#262626",
  mb: 0.75,
};

function capitalizeDisplayLabel(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function renderSelectPlaceholder(placeholder: string) {
  return (selected: string) => {
    if (!selected) {
      return (
        <Box component="span" sx={{ color: "#9ca3af" }}>
          {placeholder}
        </Box>
      );
    }
    return selected;
  };
}

export type EnhancedTaggingThenTag = {
  dimensionCode: string;
  dimensionLabel: string;
  valueCode: string;
  valueLabel: string;
};

type EnhancedTaggingRuleThenStepProps = Readonly<{
  value: EnhancedTaggingThenTag;
  onChange: (next: EnhancedTaggingThenTag) => void;
  /** API `target_type` used to load Then-step output concepts. */
  targetType: string;
  showValidation?: boolean;
}>;

export default function EnhancedTaggingRuleThenStep({
  value,
  onChange,
  targetType,
  showValidation = false,
}: EnhancedTaggingRuleThenStepProps) {
  const [conceptOptions, setConceptOptions] = useState<ConfigurableConcept[]>(
    [],
  );
  const [conceptsLoading, setConceptsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!targetType.trim()) {
      setConceptOptions([]);
      setConceptsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setConceptsLoading(true);
    fetchConditionalPlanOutputConcepts(targetType)
      .then((items) => {
        if (!cancelled) setConceptOptions(items);
      })
      .catch(() => {
        if (!cancelled) setConceptOptions([]);
      })
      .finally(() => {
        if (!cancelled) setConceptsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [targetType]);

  const dimensionMenuItems = useMemo(
    () =>
      conceptOptions.map((opt) => ({
        code: opt.code,
        name: capitalizeDisplayLabel(opt.name || opt.code),
      })),
    [conceptOptions],
  );

  const {
    input,
    setInput,
    options,
    loading: valuesLoading,
    hasMoreNext,
    loadingMore,
    loadOptions,
    loadNext,
    loadPrev,
  } = usePaginatedConceptValues(value.dimensionCode, CONCEPT_PAGE_SIZE);

  const debouncedLoadOptions = useMemo(
    () =>
      debounce((search?: string) => {
        void loadOptions(search);
      }, CONCEPT_SEARCH_DEBOUNCE_MS),
    [loadOptions],
  );

  useEffect(() => () => debouncedLoadOptions.cancel(), [debouncedLoadOptions]);

  const handleValueInputChange = useCallback(
    (_event: unknown, nextValue: string) => {
      setInput(nextValue);
      if (!nextValue.trim()) {
        debouncedLoadOptions.cancel();
        void loadOptions(undefined);
        return;
      }
      debouncedLoadOptions(nextValue);
    },
    [debouncedLoadOptions, loadOptions, setInput],
  );

  const selectedValues = useMemo<ConceptValue[]>(() => {
    if (!value.valueCode.trim()) return [];
    return [
      {
        id: value.valueCode,
        code: value.valueCode,
        name: value.valueLabel || value.valueCode,
      },
    ];
  }, [value.valueCode, value.valueLabel]);

  const displayOptions = useMemo(() => {
    if (selectedValues.length === 0) return options;
    const codes = new Set(options.map((item) => item.code));
    const missing = selectedValues.filter((item) => !codes.has(item.code));
    return missing.length === 0 ? options : [...missing, ...options];
  }, [options, selectedValues]);

  const dimensionError =
    showValidation && !value.dimensionCode.trim()
      ? "Please select a dimension."
      : undefined;
  const valueError =
    showValidation && !value.valueCode.trim()
      ? "Please select a value."
      : undefined;

  const fieldSx = { ...aiPlanFilterFieldSx, width: "100%" };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
      <Box>
        <Typography sx={formFieldLabelSx}>
          Dimension
          <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
            *
          </Box>
        </Typography>
        <FormControl fullWidth size="small" error={Boolean(dimensionError)} sx={fieldSx}>
          <Select
            displayEmpty
            value={value.dimensionCode}
            disabled={conceptsLoading || !targetType.trim()}
            MenuProps={{
              PaperProps: {
                sx: { maxHeight: 280 },
              },
            }}
            onChange={(event) => {
              const nextCode = event.target.value;
              const matched = dimensionMenuItems.find(
                (item) => item.code === nextCode,
              );
              onChange({
                dimensionCode: nextCode,
                dimensionLabel: matched?.name ?? nextCode,
                valueCode: "",
                valueLabel: "",
              });
            }}
            renderValue={(selected) => {
              if (!selected) {
                return renderSelectPlaceholder("Please select...")("");
              }
              const matched = dimensionMenuItems.find(
                (item) => item.code === selected,
              );
              return matched?.name ?? selected;
            }}
          >
            <MenuItem value="">
              <Box component="span" sx={{ color: "#9ca3af" }}>
                Please select...
              </Box>
            </MenuItem>
            {dimensionMenuItems.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.name}
              </MenuItem>
            ))}
          </Select>
          {dimensionError ? <FormHelperText>{dimensionError}</FormHelperText> : null}
        </FormControl>
      </Box>

      <Box>
        <Typography sx={formFieldLabelSx}>
          Value
          <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>
            *
          </Box>
        </Typography>
        <InfiniteScrollConceptAutocomplete
          label=""
          value={selectedValues}
          onChange={(_event, nextValue) => {
            const picked = nextValue[0];
            onChange({
              ...value,
              valueCode: picked?.code ?? "",
              valueLabel: picked
                ? capitalizeDisplayLabel(picked.name || picked.code)
                : "",
            });
          }}
          options={displayOptions}
          inputValue={input}
          onInputChange={handleValueInputChange}
          loading={valuesLoading}
          hideSelectedWhenSearching
          selectedVisibilityMode="matchWhenSearchingAllWhenEmpty"
          hasMoreNext={hasMoreNext}
          loadingMore={loadingMore}
          onLoadMoreNext={loadNext}
          onLoadMorePrev={loadPrev}
          maxSelection={1}
          hideSelectionCount
          placeholder="Please select..."
          disabled={!value.dimensionCode.trim()}
          fullWidth
          error={Boolean(valueError)}
          helperText={valueError}
          sx={fieldSx}
        />
      </Box>
    </Box>
  );
}

export function createEmptyThenTag(): EnhancedTaggingThenTag {
  return {
    dimensionCode: "",
    dimensionLabel: "",
    valueCode: "",
    valueLabel: "",
  };
}

export function isThenTagComplete(tag: EnhancedTaggingThenTag): boolean {
  return Boolean(tag.dimensionCode.trim() && tag.valueCode.trim());
}
